import { Card } from '@servicetitan/anvil2';
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDependencies } from '@servicetitan/react-ioc';
import { PrequalsApi } from '@servicetitan/financing-api-client/src/prequals-api';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { FinancedAmountStore } from '../financing-information/financedAmountStore';
import { Form } from './Form';
import { SubmitHandler } from 'react-hook-form';
import { PrequalsStore } from './prequalsStore';

export const Container = () => {
    const { saleAmount, setFinancedAmount } = FinancedAmountStore.useStore();
    const { prequalMaxAmount, setPrequalMaxAmount, operatingStates, defaultLoanAmount } =
        PrequalsStore.useStore();
    const { setupData, setSetupData } = SetupDataStore.useStore();
    const prequalificationId = setupData?.prequalificationId;
    const isGoodLeap = setupData?.provider?.toLowerCase() === 'goodleap';

    const [prequalsApi] = useDependencies(PrequalsApi);
    const { data: prequalData } = useQuery({
        queryKey: ['prequalification', prequalificationId],
        queryFn: () => prequalsApi.prequalificationsGetPrequalification(prequalificationId ?? ''),
        enabled: !!prequalificationId,
    });

    const approvedAmount = prequalData?.data.approvedAmount ?? 0;

    const defaultAmount = useMemo(() => {
        if (isGoodLeap && defaultLoanAmount != null && defaultLoanAmount > 0) {
            return defaultLoanAmount;
        }
        return saleAmount;
    }, [isGoodLeap, defaultLoanAmount, saleAmount]);

    const availableStates = useMemo(() => {
        if (isGoodLeap && operatingStates && operatingStates.length > 0) {
            return operatingStates;
        }
        return [];
    }, [isGoodLeap, operatingStates]);

    // Initialize prequalMaxAmount from approvedAmount if available, otherwise fall back to saleAmount
    // This effect should run when approvedAmount changes (from query completion) or when saleAmount changes
    useEffect(() => {
        if (approvedAmount > 0) {
            // Always prefer approvedAmount from API when available
            setPrequalMaxAmount(approvedAmount);
        } else if (prequalMaxAmount === 0 && saleAmount > 0) {
            // Only use saleAmount as fallback if we don't have approvedAmount and prequalMaxAmount hasn't been set
            setPrequalMaxAmount(saleAmount);
        }
    }, [prequalMaxAmount, approvedAmount, saleAmount, setPrequalMaxAmount]);

    const onSubmit: SubmitHandler<{ amount: number; state?: string }> = data => {
        setFinancedAmount({
            saleAmount: data.amount,
            downPayment: 0,
        });
        if (data.state?.trim() && setupData) {
            const normalizedState = data.state.trim().toUpperCase();
            setSetupData({
                ...setupData,
                state: normalizedState,
            });
        }
    };

    // Use prequalMaxAmount if it's been set, otherwise prefer approvedAmount over saleAmount
    const prequalAmount =
        prequalMaxAmount > 0 ? prequalMaxAmount : approvedAmount > 0 ? approvedAmount : saleAmount;

    const normalizedDefaultState = setupData?.state
        ? setupData.state.trim().toUpperCase()
        : undefined;

    return (
        <Card style={{ width: '100%' }}>
            <Form
                onSubmit={onSubmit}
                defaultAmount={defaultAmount}
                prequalAmount={prequalAmount}
                defaultState={normalizedDefaultState}
                availableStates={availableStates}
                isGoodLeap={isGoodLeap}
            />
        </Card>
    );
};
